import type { components, CountdownApiClient } from "@countdown/api-client";
import { type ReactElement, type ReactNode, useCallback, useState } from "react";
import { Link } from "react-router";

import { HelpTip } from "@/components/HelpTip";
import { OutcomeNotice } from "@/components/Notice";
import { Section } from "@/components/Section";
import { StatusBadge, type StatusTone } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  type ActionOutcome,
  addSponsorNote,
  LOAD_FAILED,
  runEditorialAction,
  sendAccessInvitation,
  sendInvitation,
  type SponsorDetail,
  useResource,
} from "@/lib/api";
import { AUDIT_EVENT_LABELS, can, formatDateTime } from "@/lib/labels";
import { SPONSOR_WORDING, type Wording } from "@/lib/wording";

type History = components["schemas"]["SponsorHistoryView"];
type Entry = components["schemas"]["HistoryEntryView"];
type OpenItem = components["schemas"]["OpenItemView"];
type HistoryMail = components["schemas"]["HistoryMailView"];
type SponsorMail = components["schemas"]["SponsorMailView"];

/** What each mail to a sponsor is, by its template (D93). */
export const MAIL_LABELS: Readonly<Record<string, string>> = {
  sponsor_invitation: "Einladung zum Türchen",
  sponsor_invitation_pictures: "Einladung zu den Bildern",
  sponsor_access_invitation: "Einladung zum Portal",
  sponsor_reminder: "Erinnerung an den Beitrag",
  sponsor_changes_requested: "Änderung angefordert",
  sponsor_approval_request: "Bitte um Freigabe",
  sponsor_approval_reminder: "Erinnerung an die Freigabe",
  sponsor_notice: "Hinweis",
  sponsor_lead_notice: "Neue Kontakte",
  sponsor_revocation_notice: "Widerruf von Kontakten",
  participant_prize_notification: "Gewinnmail",
  participant_prize_delivery: "Gewinn mit Nachricht",
  sponsor_prize_handover: "Gewinner übergeben",
  publisher_notice: "Interne Mitteilung",
};

const LEAD_ACCESS_LABELS: Readonly<Record<string, string>> = {
  view: "Kontaktdaten angesehen",
  export: "Kontaktdaten exportiert",
  api: "Kontaktdaten über die Schnittstelle abgerufen",
};

function sideLabel(side: Entry["side"], wording: Wording): string {
  return side === "sponsor" ? wording.nominative : side === "publisher" ? "Intern" : "System";
}

/** The state of a mail as a word and a tone; a waiting one is marked (D93). */
export function mailState(mail: HistoryMail): { readonly label: string; readonly tone: StatusTone } {
  if (mail.status === "pending" || mail.status === "sending") {
    return mail.dispatch_stopped
      ? { label: "Eingereiht, Versand angehalten", tone: "danger" }
      : { label: `Eingereiht für ${formatDateTime(mail.scheduled_for)}`, tone: "warning" };
  }
  if (mail.bounced) {
    return { label: "Unzustellbar", tone: "danger" };
  }
  if (mail.status === "failed") {
    return { label: "Nicht zugestellt", tone: "danger" };
  }
  if (mail.status === "suppressed") {
    return { label: "Nicht gesendet (gesperrt)", tone: "danger" };
  }
  return { label: "Versendet", tone: "success" };
}

/** Whether a mail never reached the sponsor: an open item of its own. */
export function undelivered(mail: HistoryMail): boolean {
  return mail.bounced || mail.status === "failed" || mail.status === "suppressed";
}

function itemTexts(wording: Wording): Readonly<Record<OpenItem["kind"], string>> {
  return {
    not_invited: "noch nicht eingeladen",
    material_missing: "Beitrag fehlt",
    review: "Beitrag wartet auf Ihre Prüfung",
    approval_waiting: `Freigabe durch den ${wording.oblique} steht aus`,
    sponsor_changes: `${wording.nominative} wünscht eine Änderung`,
    sponsor_change: `Änderung des ${wording.genitive} wartet auf Ihre Freigabe`,
    correction: `Korrektur ist noch nicht an den ${wording.oblique} gesendet`,
  };
}

/** One line of an open item: door, action, what waits and until when. */
export function itemText(item: OpenItem, wording: Wording = SPONSOR_WORDING): string {
  const due =
    item.due_at === null || item.due_at === undefined
      ? ""
      : `, Frist ${formatDateTime(item.due_at)}${item.overdue ? " (überschritten)" : ""}`;
  return `Türchen ${item.field_position} (${item.action_title}): ${itemTexts(wording)[item.kind]}${due}`;
}

function entryText(entry: Entry): string {
  switch (entry.kind) {
    case "audit": {
      const event = entry.event ?? "";
      return [AUDIT_EVENT_LABELS[event] ?? event, entry.object_label, entry.detail, entry.reason]
        .filter((part) => part !== null && part !== undefined && part !== "")
        .join(" · ");
    }
    case "lead_access":
      return `${LEAD_ACCESS_LABELS[entry.event ?? ""] ?? "Kontaktdaten abgerufen"} (${String(entry.record_count ?? 0)})`;
    case "mail":
      return `E-Mail: ${MAIL_LABELS[entry.mail?.template_key ?? ""] ?? "Nachricht"}`;
    case "note":
      return entry.note ?? "";
  }
}

/**
 * »Übersicht« of a sponsor (D93): what is open, with the step that helps at once, a note for
 * the colleagues, and everything that happened, mails with their words.
 */
export function SponsorHistory({
  detail,
  granted,
  reloadDetail,
  masterDataLink,
  wording,
}: {
  readonly wording: Wording;
  readonly detail: SponsorDetail;
  readonly granted: readonly string[];
  readonly reloadDetail: () => void;
  /** The »Stammdaten« tab, for what is fixed there (address, logo). */
  readonly masterDataLink: string;
}): ReactElement {
  const load = useCallback(
    (api: CountdownApiClient) =>
      api.GET("/api/v1/backoffice/sponsors/{sponsor_id}/history", {
        params: { path: { sponsor_id: detail.sponsor_id } },
      }),
    [detail.sponsor_id],
  );
  const history = useResource<History>(load);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [mailId, setMailId] = useState<string | null>(null);

  const run = (action: () => Promise<ActionOutcome>, done?: () => void): void => {
    setBusy(true);
    setOutcome(null);
    action()
      .then((result) => {
        setOutcome(result);
        if (result.ok) {
          done?.();
          history.reload();
          reloadDetail();
        }
      })
      .catch((error: unknown) => {
        console.error("[SponsorHistory] write failed", error);
        setOutcome({ ok: false, message: "Der Schritt konnte nicht ausgeführt werden." });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (history.status !== "ready") {
    return (
      <p className="text-sm text-muted-foreground">
        {history.status === "loading" ? "Wird geladen …" : LOAD_FAILED}
      </p>
    );
  }

  const invitable = detail.booked_doors.find((doors) => doors.invitable);
  const firmItems: ReactElement[] = [];
  if (!detail.is_own && (detail.contact_email ?? "") === "") {
    firmItems.push(
      <ItemRow key="email" text="Kontakt-E-Mail fehlt, ohne sie geht keine E-Mail hinaus.">
        <Link className="text-sm underline" to={masterDataLink}>
          Stammdaten
        </Link>
      </ItemRow>,
    );
  }
  if (!detail.is_own && detail.accounts.length === 0 && invitable !== undefined) {
    firmItems.push(
      <ItemRow key="account" text={`Niemand hat ein Konto im ${wording.portal}.`}>
        {can(granted, "sponsor_access") && (detail.contact_email ?? "") !== "" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              run(() => sendAccessInvitation(detail.sponsor_id, undefined, invitable.action_id));
            }}
          >
            Einladen
          </Button>
        ) : null}
      </ItemRow>,
    );
  }
  if (detail.logo_pending) {
    firmItems.push(
      <ItemRow key="logo" text="Das Logo wartet auf Ihre Freigabe.">
        <Link className="text-sm underline" to={masterDataLink}>
          Logo prüfen
        </Link>
      </ItemRow>,
    );
  }

  const failed = history.data.entries.filter(
    (entry) => entry.mail !== null && entry.mail !== undefined && undelivered(entry.mail),
  );

  return (
    <>
      {outcome !== null ? <OutcomeNotice outcome={outcome} className="mb-6" /> : null}

      <Section
        title={
          <span className="inline-flex items-center gap-2">
            Offene Punkte
            <HelpTip topic="Offene Punkte">
              Was beim Sponsor oder bei Ihnen wartet, für laufende und kommende Aktionen. Die
              Knöpfe lösen denselben Schritt aus wie auf der Seite des Türchens.
            </HelpTip>
          </span>
        }
      >
        {firmItems.length + history.data.open_items.length + failed.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nichts offen.</p>
        ) : (
          <ul className="divide-y">
            {firmItems}
            {history.data.open_items.map((item) => (
              <ItemRow
                key={`${item.action_id}-${String(item.field_position)}-${item.kind}`}
                text={itemText(item, wording)}
                warn={item.overdue === true}
              >
                <ItemAction item={item} granted={granted} busy={busy} run={run} />
                <Link
                  className="text-sm underline"
                  to={`/aktionen/${item.action_id}/tuerchen/${String(item.field_position)}`}
                >
                  Zum Türchen
                </Link>
              </ItemRow>
            ))}
            {failed.map((entry) => (
              <ItemRow
                key={entry.mail?.mail_queue_id}
                text={`${entryText(entry)} vom ${formatDateTime(entry.at)}: ${entry.mail === null || entry.mail === undefined ? "" : mailState(entry.mail).label}`}
                warn
              >
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setMailId(entry.mail?.mail_queue_id ?? null);
                  }}
                >
                  Ansehen
                </Button>
              </ItemRow>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Notiz">
        <form
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            run(
              () => addSponsorNote(detail.sponsor_id, note),
              () => {
                setNote("");
              },
            );
          }}
        >
          <Textarea
            aria-label="Notiz"
            maxLength={2000}
            rows={3}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            placeholder="Telefonat, Zusage, Absprache"
          />
          <div>
            <Button type="submit" disabled={busy || note.trim() === ""}>
              Notiz speichern
            </Button>
          </div>
        </form>
      </Section>

      <Section title="Verlauf">
        <HistoryEntries
          entries={history.data.entries}
          wording={wording}
          onOpenMail={setMailId}
        />
        {history.data.truncated ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Gezeigt sind die neuesten Einträge; ältere stehen im Protokoll.
          </p>
        ) : null}
      </Section>

      {mailId === null ? null : (
        <MailDialog
          load={(api) =>
            api.GET("/api/v1/backoffice/sponsors/{sponsor_id}/mails/{mail_queue_id}", {
              params: { path: { sponsor_id: detail.sponsor_id, mail_queue_id: mailId } },
            })
          }
          onClose={() => {
            setMailId(null);
          }}
        />
      )}
    </>
  );
}

/**
 * The history lines, newest first: who, when, what, and for a mail its state and »Ansehen«.
 * Shared by the sponsor's overview (D93) and the prize dialog (D95).
 */
export function HistoryEntries({
  entries,
  wording = SPONSOR_WORDING,
  onOpenMail,
}: {
  readonly entries: readonly Entry[];
  readonly wording?: Wording;
  readonly onOpenMail: (mailQueueId: string) => void;
}): ReactElement {
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">Noch nichts geschehen.</p>;
  }
  return (
    <ol className="divide-y">
      {entries.map((entry, index) => (
        <li
          key={`${entry.kind}-${entry.at}-${String(index)}`}
          className="grid gap-1 py-3 sm:grid-cols-[10rem_1fr]"
        >
          <div className="text-sm text-muted-foreground">
            <time dateTime={entry.at}>{formatDateTime(entry.at)}</time>
            <div>{sideLabel(entry.side, wording)}</div>
          </div>
          <div className="min-w-0 text-sm">
            <div className="font-medium break-words">{entry.actor_label}</div>
            <div
              className={
                entry.kind === "note" ? "break-words whitespace-pre-wrap" : "break-words"
              }
            >
              {entryText(entry)}
            </div>
            {entry.mail === null || entry.mail === undefined ? null : (
              <MailLine mail={entry.mail} onOpenMail={onOpenMail} />
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function MailLine({
  mail,
  onOpenMail,
}: {
  readonly mail: HistoryMail;
  readonly onOpenMail: (mailQueueId: string) => void;
}): ReactElement {
  const state = mailState(mail);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          onOpenMail(mail.mail_queue_id);
        }}
      >
        Ansehen
      </Button>
    </div>
  );
}

function ItemRow({
  text,
  warn = false,
  children,
}: {
  readonly text: string;
  readonly warn?: boolean;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <span className={warn ? "text-sm font-medium text-notice" : "text-sm"}>{text}</span>
      <span className="flex items-center gap-3">{children}</span>
    </li>
  );
}

/** The one step that helps at this door, where the element allows it. */
function ItemAction({
  item,
  granted,
  busy,
  run,
}: {
  readonly item: OpenItem;
  readonly granted: readonly string[];
  readonly busy: boolean;
  readonly run: (action: () => Promise<ActionOutcome>) => void;
}): ReactElement | null {
  if (item.kind === "not_invited" && can(granted, "door_invitation")) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => {
          run(() => sendInvitation(item.field_position, item.action_id));
        }}
      >
        Einladung verschicken
      </Button>
    );
  }
  if (item.kind === "approval_waiting" && can(granted, "door_review")) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => {
          run(() =>
            runEditorialAction(
              "/api/v1/backoffice/bookings/remind-sponsor",
              { field_position: item.field_position },
              item.action_id,
            ),
          );
        }}
      >
        Erinnerung senden
      </Button>
    );
  }
  return null;
}

/**
 * One mail with its words, the HTML in a sandbox without scripts. `load` names the endpoint;
 * it is read once, when the dialog opens.
 */
export function MailDialog({
  load,
  onClose,
}: {
  readonly load: (
    api: CountdownApiClient,
  ) => Promise<{ data?: SponsorMail; response: Response }>;
  readonly onClose: () => void;
}): ReactElement {
  const mail = useResource<SponsorMail>(load);
  const waiting =
    mail.status === "ready" && (mail.data.status === "pending" || mail.data.status === "sending");

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {mail.status === "ready" ? (mail.data.subject ?? MAIL_LABELS[mail.data.template_key]) : "E-Mail"}
          </DialogTitle>
          <DialogDescription>
            {mail.status !== "ready"
              ? mail.status === "loading"
                ? "Wird geladen …"
                : LOAD_FAILED
              : [
                  mail.data.recipient === null || mail.data.recipient === undefined
                    ? null
                    : `An ${mail.data.recipient}`,
                  waiting
                    ? `Eingereiht für ${formatDateTime(mail.data.at)}, noch nicht versendet`
                    : formatDateTime(mail.data.at),
                ]
                  .filter((part) => part !== null)
                  .join(" · ")}
          </DialogDescription>
        </DialogHeader>
        {mail.status !== "ready" ? null : mail.data.html !== null && mail.data.html !== undefined ? (
          <iframe
            title="Inhalt der E-Mail"
            sandbox=""
            srcDoc={mail.data.html}
            className="h-[60dvh] w-full border"
          />
        ) : mail.data.text !== null && mail.data.text !== undefined ? (
          <pre className="text-sm break-words whitespace-pre-wrap">{mail.data.text}</pre>
        ) : (
          <p className="text-sm text-muted-foreground">
            Der Inhalt lässt sich nicht mehr darstellen.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
